import type {MithraMaterializedApi} from "../../services/MithraMaterializedApi";
import type {BagStore} from "../BagStore";
import type ProfileStore from "../ProfileStore";
import {makeAutoObservable, reaction, toJS} from "mobx";
import type {
    AdvancedFilter,
    AdvancedFilterData,
    AdvancedGeoData,
    AdvancedOpportunityData,
    AdvancedSpendData,
    AdvancedSpendKPI,
    AdvancedSpendPerGroup,
    AdvancedSupplierBreakdownData,
    AdvancedTreeData,
    Category234Filter,
    CategoryFilter,
    DonutChartSupplierSpendData,
    HttpQueryParams,
    TotalSpend,
} from "../../services/ApiTypes";
import {parseAdvancedSpendPerGroup} from "../../services/ApiHelpers.ts";
import type {Country} from "../../components/visualization/bubble-map/countries";
import {countryToContinent, countryToPoint} from "../../components/visualization/bubble-map/countries";
import {HashedLoadingDataWrapper} from "../../utils/LoadingDataWrapper";
import {AxoisRequestManager, RequestManager} from "./RequestManager";
import {from, Subject, switchMap, tap} from "rxjs";
import type {BusinessUnit} from "../../services/classes/MaterializedClasses";
import type {SelectionResult} from "../../components/visualization/api-dropdown-filter/ApiDropdownTypes";
import {MultiSelection} from "../../components/visualization/api-dropdown-filter/ApiDropdownTypes";
import {toTrimmedCategoryArray} from "../../services/classes/CategoryHelpers";
import {isNonEmptyArray} from "../../utils/js-utils";
import {advancedDashboardLoader} from "../../pages/advanced/AdvancedDashboardLoader";
import {environment} from "../../env";
import {B} from "../../build";
import type {BubbleData, LatLong} from "../../components/visualization/bubble-map/BubbleMapClasses";
import {toCurrencyWithP} from "../../components/currency-component/CurrencyClasses";
import type {GeocodedRegion} from "../../pages/supplier-normalization/pages/SupplierNormalization.classes";
import type {Nullable} from "../../utils/ts-utils";
import {AxiosResponse} from "axios";
import {AdvancedBusinessUnitPivotData, AdvancedDashboardPageResponse} from "../../services/ApiTypes";

// const parseTime = d3.timeParse("%Y-%m-%d");

export const ADV_DASH_DEFAULT_PAGE_SIZE = 12;
export const ADV_DASH_PAGE_SIZE_OPTIONS = [ADV_DASH_DEFAULT_PAGE_SIZE, 25, 50, 100, 200];

export type Option = {
    value: string
    label: string
    subLabel: string
    subTotalSpend?: number
}

const SynergySplitedColors = ['blue', 'red', 'green', 'yellow']

type AdvancedRequestConfig = {
    onlyInclude: {
        [key in keyof BagAdvancedInsightsManager]?: boolean
    }
}

/**
 * The key for the Local Storage where the filters are stored.
 * When developing a unique key is used to ensure that the filters are reset.
 */
const LOCAL_STORAGE_KEY_FILTERS = `${environment.production ? '' : (B.APP_VERSION + '.')}BagAdvancedInsightsManager.filter`;

export class BagAdvancedInsightsManager {
    public databagId?: number;

    /**
     * Alternative library to handle this update complexity is TanStack query
     */
    public filters: AdvancedFilter = {}
    /**
     * Initial filter based on API
     */
    public initialFilter: AdvancedFilter | null = null
    /**
     * The filter that was ready from Local Storage
     */
    public localFilter: AdvancedFilter | null = null

    public synergySplited = false;
    public opportunity_ordering: string;
    public sup_breakdown_ordering: string;
    public bu_pivot_ordering: string;

    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    // treeData
    public advancedSpendTreeRM = new AxoisRequestManager<[number, HttpQueryParams], AdvancedTreeData[]>(([databag_id, filters]) =>
        from(this.api.getAdvancedSpendTree(databag_id, filters))
    )

    public advancedSpendTreeSplitedRMs: Array<AxoisRequestManager<[number, HttpQueryParams], AdvancedTreeData[]>> = []

    get treeData(): AdvancedTreeData {
        const result = this.advancedSpendTreeRM.result;
        if (!result) return {value: 0, label: '', children: []};
        return {
            value: result.reduce((sum, element) => sum + element.value, 0),
            label: 'All',
            children: result,
        }
    }

    get treeDataSplited(): AdvancedTreeData[] {
        return this.advancedSpendTreeSplitedRMs.map(rm => {
            if (!rm.result) return {value: 0, label: '', children: []};
            return {
                value: rm.result.reduce((sum, element) => sum + element.value, 0),
                label: 'All',
                children: rm.result,
            }
        });
    }

    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    // Country Data
    public advancedSpendCountryRM = new AxoisRequestManager<[number, HttpQueryParams], AdvancedGeoData[]>(([databag_id, filters]) =>
        from(this.api.getAdvancedSpendCountry(databag_id, filters))
    )
    public advancedSpendCountrySplitedRMs: Array<AxoisRequestManager<[number, HttpQueryParams], AdvancedGeoData[]>> = [];

    get spendCountryData(): AdvancedGeoData[] {
        return this.advancedSpendCountryRM.result ?? [];
    }

    get countryBubbleData(): BubbleData<AdvancedGeoData>[] {
        return this.spendCountryData.map(d => this.extractBubbleData(d));
    }

    private extractBubbleData(d: AdvancedGeoData): BubbleData<AdvancedGeoData> {
        const countryName = d.country || 'Unknown';
        const datafileName = d.datafile ? ', ' + d.datafile : '';
        return {
            id: d.country,
            data: d,
            point: countryToPoint(d.country),
            size: d.spend,
            tooltip: `${countryName}${datafileName}: ${toCurrencyWithP(d.spend, this.profileStore.currencyFormat, this.profileStore.currencySymbol)}`,
            color: d.color
        }
    }

    get geoBubbleDataSplited(): BubbleData<AdvancedGeoData>[] {
        return this.advancedSpendCountrySplitedRMs
            .reduce(
                (res: AdvancedGeoData[], rm, index) => res.concat(rm.result?.map(
                    agd => {
                        return {
                            ...agd,
                            datafile: this.bagStore.getBagName(rm.lastInput ? rm.lastInput[0] : 0),
                            color: SynergySplitedColors[index]
                        }
                    }
                ) ?? []), []
            )
            .map(d => this.extractBubbleData(d))
    }

    get geoDataCountryStringArr(): string[] {
        return this.spendCountryData.map(x => x.country);
    }

    public advancedSpendGeoRegionRM = new AxoisRequestManager<[number, HttpQueryParams], Array<Nullable<GeocodedRegion> & TotalSpend>>(([databag_id, filters]) =>
        from(this.api.searchInsightGeoRegions(databag_id, filters, ''))
    )

    get geoRegionBubbleData(): BubbleData<Nullable<GeocodedRegion>>[] {
        return this.advancedSpendGeoRegionRM.result?.map(d => {
            const point: LatLong | null = d.longitude === null || d.latitude === null
                ? null
                : [Number(d.longitude), Number(d.latitude)];
            const spend = Number(d.total_spend);
            const countryName = d.country_name ? ` (${d.country_name})` : '';
            return {
                id: String(d.id),
                data: d,
                point,
                size: spend,
                tooltip: `${d.region_long || 'Unknown'}${countryName}: ${toCurrencyWithP(spend, this.profileStore.currencyFormat, this.profileStore.currencySymbol)}`,
            }
        }) ?? [];
    }

    get geoRegionFilterIds(): string[] {
        if (this.geoRegionBubbleData.length === 0) return [];
        const allIds = this.geoRegionBubbleData.map(x => x.id);
        if (!this.filters.geo_regions) return allIds;
        const s = new Set(this.filters.geo_regions.selected.map(x => String(x.id)));
        if (this.filters.geo_regions.inclusive) {
            return allIds.filter(id => s.has(id));
        } else {
            return allIds.filter(id => !s.has(id));
        }
    }

    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    // spendPerSupplierData
    public advancedSpendTimePerSupplierRM = new RequestManager<[number, HttpQueryParams], AdvancedSpendPerGroup[]>(([databag_id, filters]) =>
        from(this.api.getAdvancedSpendTimePerSupplier(databag_id, filters)
            .then(resp => {
                if (this.bagStore.configStore.customerConfig?.get('ADV_DASH_GRAPHS'))
                    throw Error("Advanced dashboard graphs shouldn't be displayed but got requested");
                const elements = resp.data.map<AdvancedSpendPerGroup>(x => ({
                        s_id: x.s_id,
                        spend: x.spend,
                        date: new Date(x.date),
                        group: x.group,
                    })
                );
                this.spendPerSupplierData.updateData(elements);
                return elements
            })
        )
    )
    /**
     * Shallow observable array of AdvancedSpendPerGroup
     */
    public readonly spendPerSupplierData = new HashedLoadingDataWrapper<AdvancedSpendPerGroup>();

    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    // spendDonutData
    public advancedSpendDonutDataRM = new AxoisRequestManager<[number, HttpQueryParams], DonutChartSupplierSpendData[]>(([databag_id, filters]) =>
        from(this.api.getAdvancedDonutData(databag_id, filters))
    )
    public advancedSpendDonutDataSplitedRMs: Array<AxoisRequestManager<[number, HttpQueryParams], DonutChartSupplierSpendData[]>> = [];

    get spendDonutData(): DonutChartSupplierSpendData[] {
        return this.advancedSpendDonutDataRM.result ?? [];
    }

    get spendDonutDataSplited(): DonutChartSupplierSpendData[][] {
        return this.advancedSpendDonutDataSplitedRMs.map(rm => rm.result ?? []);
    }

    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    // spendPerActiveL1Data
    public spendPerActiveL1DataRM = new RequestManager<[number, HttpQueryParams], AdvancedSpendPerGroup[]>(([databag_id, filters]) =>
        from(this.api.getAdvancedSpendTimePerCategory(databag_id, filters, "active_l1")
            .then(resp => {
                if (!this.bagStore.configStore.customerConfig?.get('ADV_DASH_GRAPHS'))
                    throw Error("Advanced dashboard graphs shouldn't be displayed but got requested");
                const elements = resp.data.map(x => parseAdvancedSpendPerGroup(x));
                this.spendPerActiveL1Data.updateData(elements);
                return elements;
            })
        )
    )
    public spendPerActiveL1Data = new HashedLoadingDataWrapper<AdvancedSpendPerGroup>();

    get spendPerActiveL1DataStringArr(): string[] {
        if (!this.bagStore.configStore.customerConfig?.get('ADV_DASH_GRAPHS'))
            return [];
        if (!this.spendPerActiveL1DataRM.result) return []
        return Array.from(new Set(this.spendPerActiveL1DataRM.result.map((x) => x.group ? x.group : '-')))
    }

    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    // spendPerActiveL2Data
    public spendPerActiveL2DataRM = new RequestManager<[number, HttpQueryParams], AdvancedSpendPerGroup[]>(([databag_id, filters]) =>
        from(this.api.getAdvancedSpendTimePerCategory(databag_id, filters, "active_l2")
            .then(resp => resp.data.map(x => parseAdvancedSpendPerGroup(x)))
        )
    )

    get spendPerActiveL2DataStringArr(): string[] {
        if (!this.spendPerActiveL2DataRM.result) return []
        return Array.from(new Set(this.spendPerActiveL2DataRM.result.map((x) => x.group ? x.group : '-')))
    }

    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    // spendPerActiveL3Data
    public spendPerActiveL3DataRM = new RequestManager<[number, HttpQueryParams], AdvancedSpendPerGroup[]>(([databag_id, filters]) =>
        from(this.api.getAdvancedSpendTimePerCategory(databag_id, filters, "active_l3")
            .then(resp => resp.data.map(x => parseAdvancedSpendPerGroup(x)))
        )
    )

    get spendPerActiveL3DataStringArr(): string[] {
        if (!this.spendPerActiveL3DataRM.result) return []
        return Array.from(new Set(this.spendPerActiveL3DataRM.result.map((x) => x.group ? x.group : '-')))
    }

    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    // spendPerActiveL4Data
    public spendPerActiveL4DataRM = new RequestManager<[number, HttpQueryParams], AdvancedSpendPerGroup[]>(([databag_id, filters]) =>
        from(this.api.getAdvancedSpendTimePerCategory(databag_id, filters, "active_l4")
            .then(resp => resp.data.map(x => parseAdvancedSpendPerGroup(x)))
        )
    )

    get spendPerActiveL4DataStringArr(): string[] {
        if (!this.spendPerActiveL4DataRM.result) return []
        return Array.from(new Set(this.spendPerActiveL4DataRM.result.map((x) => x.group ? x.group : '-')))
    }

    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    // spendConcentrationData
    public advancedSpendConcentrationDataRM = new RequestManager<[number, HttpQueryParams], AdvancedSpendData[]>(([databag_id, filters]) =>
        from(this.api.getAdvancedSpendConcentrationData(databag_id, filters)
            .then(resp => {
                this.spendConcentrationData.setLoading(true);
                const elements = resp.data;
                this.spendConcentrationData.updateData(elements);
                this.spendConcentrationData.setLoading(false);
                return elements;
            })
        )
    )
    public advancedSpendConcentrationDataSplitedRMs: Array<RequestManager<[number, HttpQueryParams], AdvancedSpendData[]>> = [];

    public spendConcentrationData = new HashedLoadingDataWrapper<AdvancedSpendData>();

    public spendConcentrationDataSplited: HashedLoadingDataWrapper<AdvancedSpendData>[] = []

    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    // kpi
    public getAdvancedSpendKPIRM = new AxoisRequestManager<[number, HttpQueryParams], [AdvancedSpendKPI] | []>(([databag_id, filters]) =>
        from(this.api.getAdvancedSpendKPI(databag_id, filters))
    )

    get kpi(): AdvancedSpendKPI | null {
        return this.getAdvancedSpendKPIRM.result?.at(0) ?? null;
    }

    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    // opportunityData
    advancedOpportunityDataPage = 0;

    setAdvancedOpportunityDataPage: (number) => void = (value: number) => {
        this.advancedOpportunityDataPage = value;
        this.reloadData(undefined, {onlyInclude: {advancedOpportunityDataRM: true}});
    }

    advancedOpportunityDataPerPage = ADV_DASH_DEFAULT_PAGE_SIZE;

    setAdvancedOpportunityDataPerPage: (number) => void = (value: number) => {
        this.advancedOpportunityDataPerPage = value;
        this.setAdvancedOpportunityDataPage(0);
    }

    readonly advancedOpportunityDataRM =
        new RequestManager<[number, HttpQueryParams, string], AxiosResponse<AdvancedDashboardPageResponse<AdvancedOpportunityData>>>(
            ([databag_id, filters, ordering]) =>
                from(this.api.getAdvancedOpportunityData(databag_id, filters, ordering)));

    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    // businessUnitPivotData
    advancedBusinessUnitPivotDataPage = 0;

    setBusinessUnitPivotDataPage: (number) => void = (value: number) => {
        this.advancedBusinessUnitPivotDataPage = value;
        this.reloadData(undefined, {onlyInclude: {advancedBusinessUnitPivotDataRM: true}});
    }

    advancedBusinessUnitPivotDataPerPage = ADV_DASH_DEFAULT_PAGE_SIZE;

    setBusinessUnitPivotDataPerPage: (number) => void = (value: number) => {
        this.advancedBusinessUnitPivotDataPerPage = value;
        this.setBusinessUnitPivotDataPage(0);
    }

    readonly advancedBusinessUnitPivotDataRM =
        new RequestManager<[number, HttpQueryParams, string], AxiosResponse<AdvancedBusinessUnitPivotData>>(
            ([databag_id, filters, ordering]) =>
                from(this.api.getAdvancedBusinessUnitPivot(databag_id, filters, ordering)));

    get advancedBusinessUnitPivotColumns() {
        return this.advancedBusinessUnitPivotDataRM.result?.data.pivot_business_units ?? null
    }

    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    // supplierBreakdownData
    advancedSupplierBreakdownPage = 0;

    setAdvancedSupplierBreakdownPage: (number) => void = (value: number) => {
        this.advancedSupplierBreakdownPage = value;
        this.reloadData(undefined, {onlyInclude: {advancedSupplierBreakdownDataRM: true}});
    }

    advancedSupplierBreakdownPerPage = ADV_DASH_DEFAULT_PAGE_SIZE;

    setAdvancedSupplierBreakdownPerPage: (number) => void = (value: number) => {
        this.advancedSupplierBreakdownPerPage = value;
        this.setAdvancedSupplierBreakdownPage(0);
    }

    readonly advancedSupplierBreakdownDataRM =
        new RequestManager<[number, HttpQueryParams, string], AxiosResponse<AdvancedDashboardPageResponse<AdvancedSupplierBreakdownData>>>(
            ([databag_id, filters, ordering]) =>
                from(this.api.getAdvancedSupplierBreakdownData(databag_id, filters, ordering)));

    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

    public searchCDescriptionResult: Option[] | undefined = undefined;
    public searchCDescriptionSearchTerm: string = '';
    /**
     * This is needed to track options that are not in the search at the moment
     */
    public searchCDescriptionCachedOptions: Option[] = [];
    public readonly searchCDescriptionSubject: Subject<string> = new Subject();
    public readonly searchCDescriptionPipe = this.searchCDescriptionSubject.pipe(
        switchMap((searchTerm) => from(this.searchCDescription(searchTerm))),
        tap((results) => {
            this.setSearchCDescriptionSearchResults(results)
        }),
    )

    public searchPlantResult: Option[] | undefined = undefined;
    public searchPlantSearchTerm: string = '';
    /**
     * This is needed to track options that are not in the search at the moment
     */
    public searchPlantCachedOptions: Option[] = [];
    public readonly searchPlantSubject: Subject<string> = new Subject();
    public readonly searchPlantPipe = this.searchPlantSubject.pipe(
        switchMap((searchTerm) => from(this.searchPlant(searchTerm))),
        tap((results) => {
            this.setSearchPlantSearchResults(results)
        }),
    )

    public searchCAddressResult: Option[] | undefined = undefined;
    public searchCAddressSearchTerm: string = '';
    /**
     * This is needed to track options that are not in the search at the moment
     */
    public searchCAddressCachedOptions: Option[] = [];
    public readonly searchCAddressSubject: Subject<string> = new Subject();
    public readonly searchCAddressPipe = this.searchCAddressSubject.pipe(
        switchMap((searchTerm) => from(this.searchCAddress(searchTerm))),
        tap((results) => {
            this.setSearchCAddressSearchResults(results)
        }),
    )

    // noinspection JSUnusedLocalSymbols
    constructor(private api: MithraMaterializedApi, private bagStore: BagStore, private profileStore: ProfileStore) {
        makeAutoObservable(this);

        // Set values that does not need reaction
        this.opportunity_ordering = '-spend';
        this.sup_breakdown_ordering = '';
        this.bu_pivot_ordering = '';

        setTimeout(() => this.loadLocalFilter(), 0)

        // Register reactions
        reaction(() => [this.ready] as const, ([ready]) => {
            if (!ready) return;
            this.synergySplited = false;
            this.reloadData();
        });
        reaction(() => [bagStore.customerConfig, bagStore.bag, bagStore.databagConfig] as const, ([customerConfig, bag, databagConfig]) => {
            console.log('BagAdvancedInsightsManager.reaction', bag?.id, toJS(customerConfig), toJS(databagConfig));
            if (customerConfig === null || !bag || !databagConfig) {
                // Not loaded yet
                return;
                // this.initialFilter = null;
            } else {
                this.initialFilter = advancedDashboardLoader(customerConfig, bag, databagConfig, this.profileStore);
            }

            // When the bag or config has changed reset the dashboard
            this.checkFiltersLoaded();
        });
        reaction(() => ([this.ready, this.bagStore.synergy.isSynergyDatabag, this.databagId] as const), ([ready, isSynergyDatabag, databag]) => {
            if (!databag || isSynergyDatabag === null || !ready) return;
            if (isSynergyDatabag) {
                this.initSourceRMs();
            }
            this.reloadDataSplited();
        });
        reaction(() => [this.ready, this.databagId, this.opportunity_ordering] as const, ([ready, databagId, opportunity_ordering]) => {
            if (!databagId || !ready) return;
            this.advancedOpportunityDataRM.request([databagId, {
                ...this.filterQueryParams,
                page: this.advancedOpportunityDataPage,
                page_size: this.advancedOpportunityDataPerPage,
            }, opportunity_ordering]);
        });
        reaction(() => [this.ready, this.databagId, this.sup_breakdown_ordering] as const, ([ready, databagId, sup_breakdown_ordering]) => {
            if (!databagId || !ready) return;
            this.advancedSupplierBreakdownDataRM.request([databagId, {
                ...this.filterQueryParams,
                page: this.advancedSupplierBreakdownPage,
                page_size: this.advancedSupplierBreakdownPerPage
            }, sup_breakdown_ordering]);
        });
        reaction(() => [this.ready, this.databagId, this.bu_pivot_ordering] as const, ([ready, databagId, bu_pivot_ordering]) => {
            if (!databagId || !ready) return;
            this.advancedBusinessUnitPivotDataRM.request([databagId, {
                ...this.filterQueryParams,
                page: this.advancedBusinessUnitPivotDataPage,
                page_size: this.advancedBusinessUnitPivotDataPerPage
            }, bu_pivot_ordering]);
        });
    }

    get ready(): boolean {
        return Boolean(this.databagId) && this.initialFilter !== null && this.localFilter !== null;
    }

    loadLocalFilter() {
        let localFilter = {};
        const localFilterString = localStorage.getItem(LOCAL_STORAGE_KEY_FILTERS)
        if (localFilterString) {
            try {
                localFilter = JSON.parse(localFilterString);
            } catch (e) {
                console.error(e);
            }
        }
        this.localFilter = localFilter;
        this.checkFiltersLoaded()
    }

    saveFilterLocally() {
        if (this.filters !== null && this.initialFilter !== null && this.localFilter !== null) {
            localStorage.setItem(LOCAL_STORAGE_KEY_FILTERS, JSON.stringify(this.filters));
        }
    }

    discardLocallySavedFilter() {
        localStorage.removeItem(LOCAL_STORAGE_KEY_FILTERS);
    }

    checkFiltersLoaded() {
        if (this.initialFilter !== null && this.localFilter !== null) {
            this.filters = {...this.filters, ...this.initialFilter, ...this.localFilter};
            this.initialize();
        }
    }

    initialize() {
        // Initialize
        this.setSearchCDescriptionSearchTerm('')
        this.setSearchPlantSearchTerm('')
        this.setSearchCAddressSearchTerm('')
        this.reloadData();
    }

    /**
     * Parameter only_requests: If has value, specifies which requests should be made
     * For example {onlyInclude: {advancedOpportunityDataRM: true}}
     */
    reloadData(exceptData?: ('tree' | 'geo')[], only_requests?: AdvancedRequestConfig) {
        const databag = this.databagId;
        if (environment.isTestReviewPage) {
            console.debug('isTestReviewPage=true, skipping BagAdvancedInsightsManager.reloadData');
            return;
        }
        if (!databag || !this.ready) return;

        // Reload all data
        if (this.bagStore.configStore.customerConfig?.get('ADV_DASH_GRAPHS')) {
            if (!only_requests || only_requests.onlyInclude.advancedSpendTimePerSupplierRM)
                this.advancedSpendTimePerSupplierRM.request([databag, this.filterQueryParams]);
            if (!only_requests || only_requests.onlyInclude.spendPerActiveL1DataRM)
                this.spendPerActiveL1DataRM.request([databag, this.filterQueryParams]);
        }
        if (!only_requests || only_requests.onlyInclude.spendPerActiveL2DataRM)
            this.spendPerActiveL2DataRM.request([databag, this.filterQueryParams]);
        if (!only_requests || only_requests.onlyInclude.spendPerActiveL3DataRM)
            this.spendPerActiveL3DataRM.request([databag, this.filterQueryParams]);
        if (!only_requests || only_requests.onlyInclude.spendPerActiveL4DataRM)
            this.spendPerActiveL4DataRM.request([databag, this.filterQueryParams]);
        if (!only_requests || only_requests.onlyInclude.advancedSpendDonutDataRM)
            this.advancedSpendDonutDataRM.request([databag, this.filterQueryParams]);
        if (!only_requests || only_requests.onlyInclude.advancedSpendConcentrationDataRM)
            this.advancedSpendConcentrationDataRM.request([databag, this.filterQueryParams]);
        if (!only_requests || only_requests.onlyInclude.getAdvancedSpendKPIRM)
            this.getAdvancedSpendKPIRM.request([databag, this.filterQueryParams]);
        if (!only_requests || only_requests.onlyInclude.advancedOpportunityDataRM)
            this.advancedOpportunityDataRM.request([databag, {
                ...this.filterQueryParams,
                page: this.advancedOpportunityDataPage,
                page_size: this.advancedOpportunityDataPerPage,
            }, this.opportunity_ordering]);
        if (!only_requests || only_requests.onlyInclude.advancedSupplierBreakdownDataRM)
            this.advancedSupplierBreakdownDataRM.request([databag, {
                ...this.filterQueryParams,
                page: this.advancedSupplierBreakdownPage,
                page_size: this.advancedSupplierBreakdownPerPage
            }, this.sup_breakdown_ordering]);
        if (!only_requests || only_requests.onlyInclude.advancedBusinessUnitPivotDataRM)
            this.advancedBusinessUnitPivotDataRM.request([databag, {
                ...this.filterQueryParams,
                page: this.advancedBusinessUnitPivotDataPage,
                page_size: this.advancedBusinessUnitPivotDataPerPage
            }, this.bu_pivot_ordering]);

        this.reloadDataSplited(only_requests)
        if (!only_requests) {
            if (!exceptData?.includes('geo')) {
                if (this.hasGeoRegionFilter) {
                    this.advancedSpendGeoRegionRM.request([databag, this.countryFilterQueryParams]);
                } else {
                    this.advancedSpendCountryRM.request([databag, this.countryFilterQueryParams]);
                }
            }
            if (!exceptData?.includes('tree')) this.advancedSpendTreeRM.request([databag, this.treeDataFilterQueryParams]);
        }
    }

    reloadDataSplited(only_requests?: AdvancedRequestConfig) {
        // If synergy is available, load data for each source
        if (this.profileStore.opportunitySplitAllowed && (this.bagStore.synergy.srcDatabagIds?.length ?? 0) > 0)
            this.bagStore.synergy.srcDatabagIds?.forEach((src_databag_id, index) => {
                if (!only_requests || only_requests.onlyInclude.advancedSpendTreeSplitedRMs)
                    this.advancedSpendTreeSplitedRMs[index].request([src_databag_id, this.filterQueryParams]);
                if (!only_requests || only_requests.onlyInclude.advancedSpendDonutDataSplitedRMs)
                    this.advancedSpendDonutDataSplitedRMs[index].request([src_databag_id, this.filterQueryParams]);
                if (!only_requests || only_requests.onlyInclude.advancedSpendConcentrationDataSplitedRMs)
                    this.advancedSpendConcentrationDataSplitedRMs[index].request([src_databag_id, this.filterQueryParams]);
                if (!only_requests || only_requests.onlyInclude.advancedSpendCountrySplitedRMs)
                    this.advancedSpendCountrySplitedRMs[index].request([src_databag_id, this.filterQueryParams]);
            });
    }

    initSourceRMs() {
        this.spendConcentrationDataSplited.forEach(rm => rm.updateData([]));
        this.spendConcentrationDataSplited = [];
        // Call cleanup on dynamically created RMs, then set to empty array to mark for garbage collection.
        // See this for parallel processor:
        // https://github.com/mithra-ai/cat-frontend/pull/114#discussion_r1851846216
        this.advancedSpendTreeSplitedRMs.forEach(rm => rm.cleanup());
        this.advancedSpendTreeSplitedRMs = [];
        this.advancedSpendDonutDataSplitedRMs.forEach(rm => rm.cleanup());
        this.advancedSpendDonutDataSplitedRMs = [];
        this.advancedSpendConcentrationDataSplitedRMs.forEach(rm => rm.cleanup());
        this.advancedSpendConcentrationDataSplitedRMs = [];
        this.advancedSpendCountrySplitedRMs.forEach(rm => rm.cleanup());
        this.advancedSpendCountrySplitedRMs = [];
        // Create new RM for each source
        this.bagStore.synergy.srcDatabagIds?.forEach((src_databag_id) => {
            this.advancedSpendTreeSplitedRMs.push(new AxoisRequestManager<[number, HttpQueryParams], AdvancedTreeData[]>(([databag_id, filters]) =>
                from(this.api.getAdvancedSpendTree(databag_id, filters))
            ));
            this.advancedSpendDonutDataSplitedRMs.push(new AxoisRequestManager<[number, HttpQueryParams], DonutChartSupplierSpendData[]>(([databag_id, filters]) =>
                from(this.api.getAdvancedDonutData(databag_id, filters))
            ));
            this.spendConcentrationDataSplited.push(new HashedLoadingDataWrapper<AdvancedSpendData>());
            this.advancedSpendConcentrationDataSplitedRMs.push(new RequestManager<[number, HttpQueryParams], AdvancedSpendData[]>(([databag_id, filters]) =>
                from(this.api.getAdvancedSpendConcentrationData(databag_id, filters)
                    .then(resp => {
                        const elements = resp.data;
                        const srcDatabagIds = this.bagStore.synergy.srcDatabagIds;
                        if (srcDatabagIds && srcDatabagIds.indexOf(src_databag_id) >= 0)
                            this.spendConcentrationDataSplited[srcDatabagIds.indexOf(src_databag_id)].updateData(elements);
                        return elements;
                    })
                )
            ));
            this.advancedSpendCountrySplitedRMs.push(new AxoisRequestManager<[number, HttpQueryParams], AdvancedGeoData[]>(([databag_id, filters]) =>
                from(this.api.getAdvancedSpendCountry(databag_id, filters))
            ));
        });
    }

    get isLoading() {
        return this.advancedSpendTreeRM.busy;
    }

    get hasGeoRegionFilter(): boolean {
        if (!this.profileStore.p.advancedDashboardShowGeoRegionFilter) return false;
        if (!this.bagStore.databagConfig) return false;
        const supplierGeoRegion = this.bagStore.databagConfig.columns.find(c => c.column_name === 'geo_region_id' && c.table_name === 'data_source_supplier');
        if (!supplierGeoRegion) return false;
        return supplierGeoRegion.stat_count > 0;
    }

    get resetDisabled(): boolean {
        return this.filters === undefined ||
            Object.keys(this.filters).length === 0;
        // TODO CAT-2258 Fix reset conditions
        // Object.keys(this.filters).length === 0 ||
        // !Object.values(this.filters).find(filter => typeof filter === 'boolean' || (filter?.length ?? 0) > 1);
    }

    get continentFilterData(): string[] {
        const continentData: string[] = this.geoDataCountryStringArr.map((country: string) => {
            return countryToContinent(country as Country);
        });

        return Array.from(new Set(continentData));
    }

    get groupedSpendPerActiveL1FilterDataOriginal(): AdvancedFilterData[] {
        // activeL1Arr
        return BagAdvancedInsightsManager.calcGroupedSpendFilterDataOriginal(this.spendPerActiveL1Data.elements);
    }

    get groupedSpendPerActiveL2FilterDataOriginal(): AdvancedFilterData[] {
        // activeL2Arr
        return BagAdvancedInsightsManager.calcGroupedSpendFilterDataOriginal(this.spendPerActiveL2DataRM.result ?? []);
    }

    get groupedSpendPerActiveL3FilterDataOriginal(): AdvancedFilterData[] {
        // activeL3Arr
        return BagAdvancedInsightsManager.calcGroupedSpendFilterDataOriginal(this.spendPerActiveL3DataRM.result ?? []);
    }

    get groupedSpendPerActiveL4FilterDataOriginal(): AdvancedFilterData[] {
        // activeL4Arr
        return BagAdvancedInsightsManager.calcGroupedSpendFilterDataOriginal(this.spendPerActiveL4DataRM.result ?? []);
    }

    get onlyCountryFiltered() {
        return this.filters && this.ready
            && this.filters.country !== undefined
            && !MultiSelection.isSet(this.filters.s_ids)
            && !MultiSelection.isSet(this.filters.sp_ids)
            && !MultiSelection.isSet(this.filters.business_unit_ids)
            && !MultiSelection.isSet(this.filters.category_l1)
            && !MultiSelection.isSet(this.filters.geo_regions)
            && this.filters.category_l234 === undefined
            && this.filters.date_range === undefined
            && this.filters.c_descriptions === undefined
            && this.filters.plants === undefined
            && this.filters.c_address === undefined
    }

    private static toQueryParams(filter: AdvancedFilter, ...exclude: string[]): HttpQueryParams {
        const s_ids = filter.s_ids?.selected.map(o => o.id) ?? [];
        const sp_ids = filter.sp_ids?.selected.map(o => o.id) ?? [];
        const business_unit_ids = filter.business_unit_ids?.selected.map(o => o.id) ?? [];
        const category_l1s = filter.category_l1?.selected.map(o => o.id) ?? [];
        const geo_regions = filter.geo_regions?.selected.map(o => String(o.id)) ?? [];
        const params: HttpQueryParams = {
            ...filter,
            category_l1: filter.category_l1 && filter.category_l1.inclusive ? category_l1s : [],
            category_l1__not: filter.category_l1 && !filter.category_l1.inclusive ? category_l1s : [],
            category_l234: toTrimmedCategoryArray(filter.category_l234),
            s_ids: filter.s_ids && filter.s_ids.inclusive ? s_ids : [],
            s_ids__not: filter.s_ids && !filter.s_ids.inclusive ? s_ids : [],
            sp_ids: filter.sp_ids && filter.sp_ids.inclusive ? sp_ids : [],
            sp_ids__not: filter.sp_ids && !filter.sp_ids.inclusive ? sp_ids : [],
            business_unit_ids: filter.business_unit_ids && filter.business_unit_ids.inclusive ? business_unit_ids : [],
            business_unit_ids__not: filter.business_unit_ids && !filter.business_unit_ids.inclusive ? business_unit_ids : [],
            geo_regions: filter.geo_regions && filter.geo_regions.inclusive ? geo_regions : [],
            geo_regions__not: filter.geo_regions && !filter.geo_regions.inclusive ? geo_regions : [],
        };
        if (!isNonEmptyArray(params.category_l1)) delete params.category_l1;
        if (!isNonEmptyArray(params.category_l1__not)) delete params.category_l1__not;
        if (!isNonEmptyArray(params.category_l234)) delete params.category_l234;
        if (!isNonEmptyArray(params.s_ids)) delete params.s_ids;
        if (!isNonEmptyArray(params.s_ids__not)) delete params.s_ids__not;
        if (!isNonEmptyArray(params.sp_ids)) delete params.sp_ids;
        if (!isNonEmptyArray(params.sp_ids__not)) delete params.sp_ids__not;
        if (!isNonEmptyArray(params.business_unit_ids)) delete params.business_unit_ids;
        if (!isNonEmptyArray(params.business_unit_ids__not)) delete params.business_unit_ids__not;
        if (!isNonEmptyArray(params.geo_regions)) delete params.geo_regions;
        if (!isNonEmptyArray(params.geo_regions__not)) delete params.geo_regions__not;
        exclude.forEach(key => {
            if (key in params) {
                delete params[key];
            }
        })
        return params;
    }

    get filterQueryParams(): HttpQueryParams {
        return BagAdvancedInsightsManager.toQueryParams(this.filters);
    }

    get filterQueryParamsSupplier(): HttpQueryParams {
        return BagAdvancedInsightsManager.toQueryParams(this.filters, 's_ids', 's_ids__not');
    }

    get filterQueryParamsParentSupplier(): HttpQueryParams {
        return BagAdvancedInsightsManager.toQueryParams(this.filters, 'sp_ids', 'sp_ids__not');
    }

    get filterQueryParamsBusinessUnits(): HttpQueryParams {
        return BagAdvancedInsightsManager.toQueryParams(this.filters, 'business_unit_ids', 'business_unit_ids__not');
    }

    get filterQueryParamsL1(): HttpQueryParams {
        return BagAdvancedInsightsManager.toQueryParams(this.filters, 'category_l1s', 'category_l1s__not');
    }

    get treeDataFilterQueryParams(): HttpQueryParams {
        return BagAdvancedInsightsManager.toQueryParams(this.filters, 'categories');
    }

    get filterQueryParamsGeoRegions(): HttpQueryParams {
        return BagAdvancedInsightsManager.toQueryParams(this.filters, 'geo_regions', 'geo_regions__not');
    }

    get countryFilterQueryParams(): HttpQueryParams {
        // When only the countries are filtered, do not hide the other countries
        return this.onlyCountryFiltered ? {} : this.filterQueryParams;
    }

    setDatabagId(databagId: number | undefined) {
        console.log('BagAdvancedInsightsManager.setDatabagId', databagId, this.databagId);
        this.databagId = databagId;
    }

    setSearchCDescriptionSearchTerm(searchTerm: string) {
        this.searchCDescriptionSearchTerm = searchTerm;
        this.searchCDescriptionSubject.next(searchTerm);
    }

    setSearchCAddressSearchTerm(searchTerm: string) {
        this.searchCAddressSearchTerm = searchTerm;
        this.searchCAddressSubject.next(searchTerm);
    }

    setSearchCDescriptionSearchResults(response: string[]) {
        this.searchCDescriptionResult = response.map<Option>((cc_description) => ({
            value: String(cc_description),
            label: cc_description || '(Empty)',
            subLabel: '',
        }))
    }

    setSearchCAddressSearchResults(response: string[]) {
        this.searchCAddressResult = response.map<Option>((c_address) => ({
            value: String(c_address),
            label: c_address || '(Empty)',
            subLabel: '',
        }))
    }

    setSearchCDescriptionCachedOptions(cachedOptions: Option[]) {
        this.searchCDescriptionCachedOptions = cachedOptions;
    }

    setSearchPlantSearchTerm(searchTerm: string) {
        this.searchPlantSearchTerm = searchTerm;
        this.searchPlantSubject.next(searchTerm);
    }

    setSearchPlantSearchResults(response: string[]) {
        this.searchPlantResult = response.map<Option>((cc_description) => ({
            value: String(cc_description),
            label: cc_description || '(Empty)',
            subLabel: '',
        }))
    }

    setSearchPlantCachedOptions(cachedOptions: Option[]) {
        this.searchPlantCachedOptions = cachedOptions;
    }

    setSearchCAddressCachedOptions(cachedOptions: Option[]) {
        this.searchCAddressCachedOptions = cachedOptions;
    }

    setSynergySplited(value: boolean) {
        this.synergySplited = value;
    }

    setOnlyCommonCategoriesFilter(value: boolean) {
        this.filters.only_common_categories = value;
        this.saveFilterLocally();
        this.reloadData(undefined, {onlyInclude: {advancedOpportunityDataRM: true}});
    }

    setOnlyCommonSuppliersFilter(value: boolean) {
        this.filters.only_common_suppliers = value;
        this.saveFilterLocally();
        this.reloadData(undefined, {onlyInclude: {advancedSupplierBreakdownDataRM: true}});
    }

    set groupParentSuppliersFilter(value: boolean) {
        this.filters.group_by_parent_supplier = value;
        this.saveFilterLocally();
        if (!this.synergySplited)
            this.reloadData(undefined, {
                onlyInclude: {
                    advancedSpendDonutDataRM: true,
                    advancedSpendConcentrationDataRM: true,
                    advancedSupplierBreakdownDataRM: true,
                }
            });
        else
            this.reloadDataSplited({
                onlyInclude: {
                    advancedSpendDonutDataSplitedRMs: true,
                    advancedSpendConcentrationDataSplitedRMs: true,
                    advancedSupplierBreakdownDataRM: true, // TODO: @Moj there is no splitted variant of this?
                }
            });
    }

    /**
     * Show dashboard by parent suppliers instead of suppliers
     */
    get groupParentSuppliersFilter(): boolean {
        return Boolean(this.filters.group_by_parent_supplier);
    }

    setOrderingWithPrefix(ordering: string, prefix: string) {
        if (prefix === 'opportunity_')
            this.opportunity_ordering = ordering;
        else if (prefix === 'sup_breakdown_')
            this.sup_breakdown_ordering = ordering;
        else if (prefix === 'bu_pivot_')
            this.bu_pivot_ordering = ordering;
        return;
    }

    getOrderingWithPrefix(prefix: string) {
        if (prefix === 'opportunity_')
            return this.opportunity_ordering;
        else if (prefix === 'sup_breakdown_')
            return this.sup_breakdown_ordering;
        else if (prefix === 'bu_pivot_')
            return this.bu_pivot_ordering;
        return '';
    }

    getActiveL1Spend(lookup: string): number {
        return BagAdvancedInsightsManager.calcActiveSpend(this.groupedSpendPerActiveL1FilterDataOriginal, lookup);
    }

    getActiveL2Spend(lookup: string): number {
        return BagAdvancedInsightsManager.calcActiveSpend(this.groupedSpendPerActiveL2FilterDataOriginal, lookup);
    }

    getActiveL3Spend(lookup: string): number {
        return BagAdvancedInsightsManager.calcActiveSpend(this.groupedSpendPerActiveL3FilterDataOriginal, lookup);
    }

    getActiveL4Spend(lookup: string): number {
        return BagAdvancedInsightsManager.calcActiveSpend(this.groupedSpendPerActiveL4FilterDataOriginal, lookup);
    }

    reset() {
        if (this.initialFilter === null || this.localFilter === null) {
            console.warn('BagAdvancedInsightsManager.reset(): filters are not loaded yet...');
            return;
        }
        this.discardLocallySavedFilter();
        this.setSearchCDescriptionSearchTerm('')
        this.setSearchPlantSearchTerm('')
        this.setSearchCAddressSearchTerm('')
        this.filters = {...this.initialFilter};
        this.reloadData();
    }

    dateFilterChanged = (startDate: string | undefined, endDate: string | undefined) => {
        this.filters.date_range = startDate && endDate ? [startDate, endDate] : undefined;
        this.saveFilterLocally()
        this.reloadData();
    }

    get singleSelectedCategory(): CategoryFilter | null {
        if (MultiSelection.isIncludingOne(this.filters.category_l1)) {
            const l1 = this.filters.category_l1?.selected[0].id as string;
            if (this.filters.category_l234) {
                return [l1, ...this.filters.category_l234] as CategoryFilter
            } else {
                return [l1] as CategoryFilter
            }
        }
        return null;
    }

    categoryL1FilterChanged = (selection: SelectionResult<string>) => {
        this.filters.category_l1 = selection;
        this.saveFilterLocally();
        this.reloadData();
    }

    category234FilterChanged = (category_l234: Category234Filter | null) => {
        this.filters.category_l234 = category_l234 && category_l234.length > 0 ? category_l234 : undefined;
        this.saveFilterLocally();
        this.reloadData();
    }

    onTreeMapCategoryFilterChanged = (categories: CategoryFilter | null) => {
        // Update all other data elements, except the tree map
        const l1 = categories?.[0];
        this.filters.category_l1 = {inclusive: true, selected: l1 ? [{id: l1}] : []};
        this.filters.category_l234 = categories && categories.length > 1 ? categories.splice(1) : undefined;
        this.saveFilterLocally();
        this.reloadData(['tree']);
    }

    countryFilterChangedNew = (selectedCountries: string[]) => {
        this.filters.country = Array.from(new Set(selectedCountries));
        this.saveFilterLocally();
        this.reloadData();
    }

    countryFilterChanged = (country: string[]) => {
        this.filters.country = country && country.length > 0 ? country : undefined;
        this.saveFilterLocally();
        this.reloadData();
    }

    supplierFilterChanged = (selection: SelectionResult<string>) => {
        this.filters.s_ids = selection;
        this.saveFilterLocally();
        this.reloadData();
    }

    parentSupplierFilterChanged = (selection: SelectionResult<string>) => {
        this.filters.sp_ids = selection;
        this.saveFilterLocally();
        this.reloadData();
    }

    businessUnitFilterChanged = (selection: SelectionResult<number>) => {
        this.filters.business_unit_ids = selection;
        this.saveFilterLocally();
        this.reloadData();
    }

    CDescriptionFilterChanged = (c_descriptions?: string[]) => {
        this.filters.c_descriptions = c_descriptions && c_descriptions.length > 0 ? c_descriptions : undefined;
        this.saveFilterLocally();
        this.reloadData();
    }

    PlantFilterChanged = (plants?: string[]) => {
        this.filters.plants = plants && plants.length > 0 ? plants : undefined;
        this.saveFilterLocally();
        this.reloadData();
    }

    CAddressFilterChanged = (c_address?: string[]) => {
        this.filters.c_address = c_address && c_address.length > 0 ? c_address : undefined;
        this.saveFilterLocally();
        this.reloadData();
    }

    geoRegionFilterChanged = (selection: SelectionResult<number | null>) => {
        this.filters.geo_regions = selection;
        this.saveFilterLocally();
        this.reloadData();
    }

    geoRegionFilterChangedByBubbleMap = (selection: string[]) => {
        if (selection && selection.length > 0) {
            const selected = this.geoRegionBubbleData
                .filter(d => selection.includes(d.id))
                .map(d => ({id: (d.id === 'null' ? null : Number(d.id))}));
            this.filters.geo_regions = {inclusive: true, selected}
        } else {
            this.filters.geo_regions = {inclusive: false, selected: []};
        }
        this.saveFilterLocally();
        this.reloadData();
    }

    breakdownFilterChanged = (row?: AdvancedSupplierBreakdownData) => {
        this.filters.s_ids = row?.s_id ? MultiSelection.inclusiveSet(this.filters.s_ids, row.s_id) ?? undefined : undefined;
        this.filters.sp_ids = row?.sp_id ? MultiSelection.inclusiveSet(this.filters.sp_ids, row.sp_id) ?? undefined : undefined;
        this.filters.business_unit_ids = row?.business_units ? MultiSelection.inclusiveSetAll(this.filters.business_unit_ids, row.business_unit_ids) ?? undefined : undefined;
        this.filters.country = row?.s_country_code ? [row.s_country_code] : undefined;
        this.filters.category_l1 = row?.active_l1 ? {inclusive: true, selected: [{id: row.active_l1}]} : undefined;
        this.filters.category_l234 = (row?.active_l2 || row?.active_l3 || row?.active_l4)
            ? [row.active_l2, row.active_l3, row.active_l4]
            : undefined;
        // TODO [CAT-2098]: How to deal with click on breakdown table (w.r.t. cc_description and business_unit_ids?)
        //  BU_Id is used in filter, but not send by BE
        // TODO: Is this needed? this.filters.c_descriptions = row?.c_description ? [row.c_description] : undefined;
        // c_address needed as well?
        this.saveFilterLocally();
        this.reloadData();
    }

    // async searchParentSupplier(parentSupplier: string): Promise<(ParentSupplierSearchResults & { total_spend: number })[]> {
    //     const databagId = this.profileStore.p.hardcodedCombinedBagId ?? this.databagId;
    //     if (!databagId) throw new Error('DatabagId or hardcodedCombinedBagId is not set');
    //     const params = BagAdvancedInsightsManager.toQueryParams(this.filters, 'sp_ids');
    //     const response = await this.api.searchInsightParentSupplier(databagId, params, parentSupplier);
    //     return response.data.map<ParentSupplierData>(d => ({
    //         ...d,
    //         total_spend: Number(d.total_spend),
    //     }));
    // }

    async searchBusinessUnit(businessUnit: string): Promise<BusinessUnit[]> {
        if (!this.databagId || !this.ready) throw new Error('DatabagId is not set');
        // TODO: We do not allow search for now
        const response = await this.api.listBusinessUnitData(this.databagId);
        return response.data;
    }

    async searchCDescription(search: string): Promise<string[]> {
        if (!this.databagId || !this.ready) throw new Error('DatabagId is not set');
        const params = BagAdvancedInsightsManager.toQueryParams(this.filters, 'c_descriptions');
        const response = await this.api.searchInsightCDescriptions(this.databagId, params, search);
        return response.data;
    }

    async searchPlant(search: string): Promise<string[]> {
        if (!this.databagId || !this.ready) throw new Error('DatabagId is not set');
        const params = BagAdvancedInsightsManager.toQueryParams(this.filters, 'plants');
        const response = await this.api.searchInsightPlants(this.databagId, params, search);
        return response.data;
    }

    async searchCAddress(search: string): Promise<string[]> {
        if (!this.databagId || !this.ready) throw new Error('DatabagId is not set');
        const params = BagAdvancedInsightsManager.toQueryParams(this.filters, 'c_address');
        const response = await this.api.searchInsightCAddress(this.databagId, params, search);
        return response.data;
    }

    private static calcGroupedSpendFilterDataOriginal(data: AdvancedSpendPerGroup[]): AdvancedFilterData[] {
        const resultMap = new Map<string, AdvancedFilterData>();

        data.forEach((current: AdvancedSpendPerGroup) => {
            if (current.group === '') {
                return;
            }
            const existing = resultMap.get(current.group);
            if (existing) {
                existing.spend += current.spend;
            } else {
                resultMap.set(current.group, {id: current.s_id, name: current.group, spend: current.spend});
            }
        });

        return Array.from(resultMap.values());
    }

    private static calcGroupedSpendFilterData(data: AdvancedSpendPerGroup[]): string[] {
        const resultMap = new Map<string, AdvancedFilterData>();

        data.forEach((current: AdvancedSpendPerGroup) => {
            if (current.group === '') {
                return;
            }
            const existing = resultMap.get(current.group);
            if (existing) {
                existing.spend += current.spend;
            } else {
                resultMap.set(current.group, {id: current.s_id, name: current.group, spend: current.spend});
            }
        });

        const supplierIds: (string | undefined)[] = Array.from(resultMap.values()).map(item => item.id ? item.id.toString() : undefined);
        return supplierIds.filter(id => id !== undefined) as string[];
    }

    public static calcActiveSpend(data: AdvancedFilterData[], lookup: string): number {
        const obj = data.find((item) => item.name === lookup);
        return obj?.spend || 0;
    }

    public static getCountrySpend(geoData: AdvancedGeoData[], countryId: string): number {
        const countryArr = geoData.map(x => ({name: x.country, spend: x.spend}))

        const country = countryArr.find((country) => country.name == countryId);
        return country?.spend || 0;
    }

    public static calculateContinentSpend(geoData: AdvancedGeoData[], continent: string): number {
        const continentData: AdvancedGeoData[] = geoData.filter(
            (country) => countryToContinent(country.country) === continent
        );

        return continentData.reduce((total, country) => {
            return total + BagAdvancedInsightsManager.getCountrySpend(geoData, country.country);
        }, 0);
    };
}
